import React, { useEffect, useState, useCallback } from "react";
import FloorPlanner from "./components/FloorPlanner";
import Sidebar from "./components/Sidebar";
import { v4 as uuidv4 } from "uuid";
import { auth } from '../../firebase';
import { useNavigate, useLocation } from 'react-router-dom';
import { createUserEquipment, updateUserEquipment, createMasterEquipment, updateMasterEquipment } from '../../services/api';

const INITIAL_FLOOR = '1F';

export default function HouserPlanner() {
  const location = useLocation();
  const navigate = useNavigate();

  const [floorRooms, setFloorRooms] = useState(location.state?.floorRooms || {'1F': [], '2F': []});
  const [currentFloor, setCurrentFloor] = useState(INITIAL_FLOOR);
  const [selectedId, setSelectedId] = useState(null);
  const [targetUserId, setTargetUserId] = useState(location.state?.targetUserId || auth.currentUser?.uid || null);
  const [mode, setMode] = useState(location.state?.mode || 'create');
  const [user_equipment_id, setUserEquipmentId] = useState(location.state?.user_equipment_id || null);
  const [item_id, setItemId] = useState(location.state?.item_id || null);

  // 現在のフロアの部屋リストとそのセッター
  const roomsForCurrentFloor = floorRooms[currentFloor] || [];
  const setRoomsForCurrentFloor = (updater) => {
    setFloorRooms(prevFloorRooms => {
      const currentRoomsList = prevFloorRooms[currentFloor] || [];
      const newRoomsList = typeof updater === 'function' ? updater(currentRoomsList) : updater;
      return {
        ...prevFloorRooms,
        [currentFloor]: newRoomsList,
      };
    });
  };

  const handleAddRoom = (roomType) => {
    const newRoom = {
      id: uuidv4(),
      x: roomType.x || 100,
      y: roomType.y || 100,
      x2: roomType.x2 || 200,
      y2: roomType.y2 || 100,
      width: roomType.width || 100, // サイドバーで定義された幅を使用、なければデフォルト
      height: roomType.height || 100, // サイドバーで定義された高さを使用、なければデフォルト
      name: roomType.name,
      fill: roomType.fill || "#add8e6",
      image: roomType.image || null, // ★ 画像パスも追加 (roomオブジェクトにimageプロパティがある場合)
      rotation: 0,
      type: roomType.type || 'room',
      isFreeSize: roomType.isFreeSize || false,
      strokeWidth: roomType.strokeWidth || 8,
    };
    setRoomsForCurrentFloor((prev) => [...prev, newRoom]);
    setSelectedId(newRoom.id);
  };

  const handleRotateRoom = (roomId, angleIncrement) => {
    setRoomsForCurrentFloor((prevRoomsList) =>
      prevRoomsList.map((room) => {
        if (room.id === roomId) {
          const currentRotation = room.rotation || 0;
          let newRotation = (currentRotation + angleIncrement) % 360;
          if (newRotation < 0) {
            newRotation += 360;
          }
          return { ...room, rotation: newRotation };
        }
        return room;
      })
    );
  };

  // 削除処理 (ボタンとキーボードで共通化)
  const handleDelete = useCallback(() => {
    if (selectedId) {
      setFloorRooms(prevFloorRooms => {
        const currentRoomsList = prevFloorRooms[currentFloor] || [];
        const newRoomsList = currentRoomsList.filter((room) => room.id !== selectedId);
        return {
          ...prevFloorRooms,
          [currentFloor]: newRoomsList,
        };
      });
      setSelectedId(null);
    }
  }, [selectedId, currentFloor, setFloorRooms]);

  const handleChangeFloor = (floor) => {
    setCurrentFloor(floor);
    setSelectedId(null); // フロア切り替え時に選択を解除
  };

  const handleSaveAndClose = async () => {
    if (mode === 'create') {
      await createUserEquipment('floorplan', {
        item_type: 'floorplan',
        user_id: targetUserId,
        rooms: floorRooms,
      });
    } else if (mode === 'edit') {
      await updateUserEquipment('floorplan', user_equipment_id, {
        user_id: targetUserId,
        rooms: floorRooms,
      });
    } else if(mode === 'new_master'){
      await createMasterEquipment('floorplan', {
        rooms: floorRooms,
      });
    } else if (mode === 'edit_master') {
      await updateMasterEquipment('floorplan', item_id, {
        rooms: floorRooms,
      });
    }
    navigate(-1)
  };

  const updateSelectedRoom = (room) => {
    setRoomsForCurrentFloor((prevRoomsList) =>
      prevRoomsList.map((r) => (r.id === room.id ? room : r))
    );
  };

  // targetUserId が未設定の場合、ログインユーザーのUIDをセットする
  useEffect(() => {
    if (!targetUserId && auth.currentUser) {
      setTargetUserId(auth.currentUser.uid);
    }
    // location.state.targetUserId が変更された場合にも追従
    if (location.state?.targetUserId && location.state.targetUserId !== targetUserId) {
      setTargetUserId(location.state.targetUserId);
    }
  }, [targetUserId, location.state?.targetUserId]);

  // --- ここから追加 ---
  // キーボードイベントの処理
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Delete" || event.key === "Backspace") {
        if (selectedId) {
          handleDelete();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedId, handleDelete]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        <Sidebar
          onAddRoom={handleAddRoom}
          rooms={roomsForCurrentFloor} // Sidebarにroomsを渡す (現在はカレントフロアの部屋)
          selectedId={selectedId} // SidebarにselectedIdを渡す
          onRotateRoom={handleRotateRoom} // 回転ハンドラを渡す
          updateSelectedRoom={updateSelectedRoom} // 選択された部屋を更新するハンドラを渡す
        />
        <FloorPlanner
          rooms={roomsForCurrentFloor} // 現在のフロアの部屋を渡す
          setRooms={setRoomsForCurrentFloor} // 現在のフロアの部屋を更新する関数を渡す
          selectedId={selectedId}
          setSelectedId={setSelectedId}
        />
      </div>

      {/* 下部：削除ボタン */} {/* ←ユーザーのコメントを復元 */}
      <div
        style={{
          padding: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: "1px solid #ccc",
          backgroundColor: "#f8f8f8",
        }}
      >
        {/* フロア切り替えボタン (家の形が選択されている場合のみ表示) */}
          <div>
            <button
              onClick={() => handleChangeFloor('1F')}
              disabled={currentFloor === '1F'}
              style={{ marginRight: 8, padding: "8px 12px", fontSize: "14px", cursor: "pointer", border: currentFloor === '1F' ? "2px solid #3182ce" : "1px solid #ccc", backgroundColor: currentFloor === '1F' ? "#ebf8ff" : "white" }}
            >
              1F
            </button>
            <button
              onClick={() => handleChangeFloor('2F')}
              disabled={currentFloor === '2F'}
              style={{ padding: "8px 12px", fontSize: "14px", cursor: "pointer", border: currentFloor === '2F' ? "2px solid #3182ce" : "1px solid #ccc", backgroundColor: currentFloor === '2F' ? "#ebf8ff" : "white" }}
            >
              2F
            </button>
          </div>
        {/* 家の形が選択されていない場合は、フロアボタンの代わりに空のdivを配置してレイアウトを維持 */}
        <div style={{ minWidth: '100px' }}></div> {/* ボタン2つ分程度の間隔を確保 */}

        {/* 保存して閉じるボタン */}
        <button
          onClick={handleSaveAndClose}
          style={{
            backgroundColor: "#000000",
            color: "#FFFFFF",
            padding: "8px 16px",
            fontSize: "16px",
            border: "none",
            borderRadius: 4,
            cursor: "pointer",
          }}
        >
          保存して閉じる
        </button>
      </div>
    </div>
  );
}
